/**
 * PageLayoutDefault: The default layout that most pages use, with a gray header and one or more white body sections.
 *  - Examples: Actions Listing, Action Details, Governance Listing, Governance Details.
 *
 * PageLayoutDefaultGridAndTable: A layout that uses the standard gray header, but with a grid and table layout instead of a series of body sections.
 *  - Examples: TPRM Third Party Listing, TPRM Third Party Service Listing.
 *
 * PageLayoutDashboard: A layout for full-page dashboards WITHOUT tabs.
 *  - Examples: TPRM Third Party Service Dashboard.
 *
 * PageLayoutDashboardTabs: A layout for full-page dashboards WITH tabs.
 *  - Examples: The overall "Dashboard" page.
 *
 * PageLayoutHybridDashboard: This is essentially a layout hybrid of PageLayoutDefault and PageLayoutDashboard. The header is a dashboard, while the body is the standard series of body sections.
 *  - Examples: Control Details, Risk Details.
 *
 * PageLayoutHybridDashboardGridAndTable: This is similar to PageLayoutHybridDashboard, but the content is a grid of cards and a table instead of a series of body sections.
 *  - Examples: Control Group Details. The Framework Details page manually implements this (dashboard header with grid and table body), since it has tabs which make it a unique layout.
 *
 * PageLayoutQuestionnaire: A layout for a questionnaire with multiple sections. Includes a header (with optional dashlets), a fixed footer, and a sidebar for selecting the current section.
 *  - Examples: The Third-Party Due Diligence Questionnaire and the Third-Party Due Diligence Assessment.
 */

import { type JSX, type ReactNode, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { FixedFooter, FixedFooterProps } from 'Components/FixedFooter/FixedFooter';
import { Dashlet, DashletProps } from 'Components/PageLayout/Dashlet/Dashlet';
import { Text } from 'Components/Text/Text';

import styles from './PageLayoutQuestionnaire.module.css';

export interface PageLayoutQuestionnaireProps {
    headerDashlets: DashletProps[];
    headerTitle: string;
    sidebarSections: SidebarSection[];
    sidebarTitle: string;

    headerBreadcrumb?: React.ReactNode;
    headerButtons?: React.ReactNode;
    headerDescription?: React.ReactNode | string;
    /**
     * If this prop is defined, it is called when the user selects a new section from the sidebar. Useful for interrupting the user's action if there are unsaved changes to the current section.
     * TODO: Maybe the "choose to save or discard changes" modal should be built into this page layout component?
     */
    onBeforeSectionChange?: (fromIndex: number, toIndex: number) => Promise<boolean>;
}

/** Resolves the section index encoded in the URL fragment, falling back to the first section if the fragment is absent, non-numeric, or out of range. */
const parseSectionIndexFromHash = (hash: string, sectionCount: number): number => {
    const parsedIndex = Number(hash.substring(1));
    return Number.isInteger(parsedIndex) && parsedIndex >= 0 && parsedIndex < sectionCount ? parsedIndex : 0;
};

/**
 * Renders a page with a header and dashlets (if any), a fixed footer for saving changes (if applicable to selected content), and a sidebar for selecting the current section of content to show the user (functions much like our main navigation menu).
 * Used for third-party due diligence assessments (accessible by internal TPRM users) and third-party due diligence questionnaires (accessible by third-party service contacts).
 *
 * The selected section is recorded in the URL fragment (`#0`, `#1`, and so on) so that it survives a page refresh or the user navigating back to the page.
 * Indicies are used for simplicity; this is doable because sections are not dynamic. If this component does ever need to be used with dynamic sections, this approach will need to be changed.
 */
export const PageLayoutQuestionnaire = (props: PageLayoutQuestionnaireProps) => {
    const location = useLocation();
    const navigate = useNavigate();

    const [selectedSectionIndex, setSelectedSectionIndex] = useState(() => parseSectionIndexFromHash(location.hash, props.sidebarSections.length));
    const activeSection = props.sidebarSections[selectedSectionIndex];

    /**
     * Keeps the URL fragment in sync with the selected section.
     * The navigation replaces the current history entry so that selecting a section does not add an entry the user has to click "Back" through, and so that clicking "Back" can never change the selected section without passing through `onBeforeSectionChange`.
     */
    useEffect(() => {
        const hashForSelectedSection = `#${selectedSectionIndex}`;
        if (location.hash !== hashForSelectedSection) {
            navigate(`${location.pathname}${location.search}${hashForSelectedSection}`, { replace: true });
        }
    }, [location.hash, location.pathname, location.search, navigate, selectedSectionIndex]);

    const handleSelectSection = async (toIndex: number) => {
        if (toIndex === selectedSectionIndex) {
            return;
        }

        if (props.onBeforeSectionChange) {
            const allowed = await props.onBeforeSectionChange(selectedSectionIndex, toIndex);
            if (!allowed) {
                return;
            }
        }

        setSelectedSectionIndex(toIndex);
    };

    return (
        <>
            <Header title={props.headerTitle} breadcrumb={props.headerBreadcrumb} dashlets={props.headerDashlets} description={props.headerDescription} />
            <div className={styles.contentWrapper}>
                <div className={styles.mainColumn}>
                    <Body pageBodySections={activeSection.content} />
                </div>
                <Sidebar onSelectSection={handleSelectSection} sections={props.sidebarSections} selectedSectionIndex={selectedSectionIndex} title={props.sidebarTitle} />
            </div>
            {activeSection.footerProps && <FixedFooter {...activeSection.footerProps} />}
        </>
    );
};

interface HeaderProps {
    title: string;

    breadcrumb?: ReactNode;
    description?: ReactNode | string;
    dashlets: DashletProps[];
}

const Header = (props: HeaderProps): JSX.Element => {
    return (
        <div className={styles.background}>
            <div className={styles.pageHeaderContent}>
                {props.breadcrumb}
                <div className={styles.titleAndButtons}>
                    <Text color="white" variant="Header1">
                        {props.title}
                    </Text>
                </div>
                {typeof props.description === 'string' ? (
                    <Text color="darkGray" variant="Header3">
                        {props.description}
                    </Text>
                ) : (
                    props.description
                )}
                <div className={styles.dashlets}>
                    {props.dashlets.map((dashlet, index) => (
                        <Dashlet key={index} {...dashlet} />
                    ))}
                </div>
            </div>
        </div>
    );
};

/**
 * @param title     - The title of the page body section. It is useful to include this if there are multiple page body sections on the page to differentiate them, or if it provides more context to the page header. But if the page body section contains tabs (so the tab names would make this redundant), or if this would simply be the same as the page header, then it should be excluded.
 * @param subtitle  - The subtitle of the page body section. Displayed in dark gray text.
 * @param buttons   - Non-form buttons that are right-justified but on the same vertical alignment as the body section title. NOTE: This should only be used when there are multiple body sections or tabs where non-form buttons are needed that only apply to one body section. Otherwise, non-form buttons go in the page header. Buttons that affect a form should be part of the form itself and are neither implemented nor documented in this module.
 * @param content   - The content of the page body section.
 */
interface BodySectionProps {
    title?: string;
    subtitle?: string;
    buttons?: React.ReactNode;
    content: React.ReactNode;
}

/**
 * A page body section is a grouping of content within a page. It is the familiar white box with rounded corners. Usually there is one of these per page, but there can be multiple.
 */
const BodySection = (props: BodySectionProps): JSX.Element => {
    const buildTitleAndButtons = () => {
        if (props.title && props.buttons) {
            return (
                <>
                    <div className={styles.titleAndButtons}>
                        <Text noStyles variant="Header2">
                            {props.title}
                        </Text>
                        <div className={styles.buttons}>{props.buttons}</div>
                    </div>
                    {props.subtitle && <Text color="darkGray">{props.subtitle}</Text>}
                    <hr />
                </>
            );
        } else if (props.title) {
            return (
                <>
                    <Text noStyles variant="Header2">
                        {props.title}
                    </Text>
                    {props.subtitle && <Text color="darkGray">{props.subtitle}</Text>}
                    <hr />
                </>
            );
        }
    };

    return (
        <div className={styles.pageBodySection}>
            {buildTitleAndButtons()}
            {props.content}
        </div>
    );
};

/**
 * @param pageBodySections - The page body sections that comprise the page body.
 */
interface BodyProps {
    pageBodySections: BodySectionProps[];
}

/**
 * The page body is everything below the page header. It is the main content of the page, and is composed of one or more page body sections.
 */
const Body = (props: BodyProps): JSX.Element => {
    return (
        <div className={styles.pageBody}>
            {props.pageBodySections.map((pageBodySection, index) => (
                <BodySection key={index} {...pageBodySection} />
            ))}
        </div>
    );
};

export interface SidebarSection {
    content: BodySectionProps[];
    title: string;

    additionalContent?: JSX.Element;
    footerProps?: FixedFooterProps;
    /** Optional metadata for parent component use (e.g., identifiers for saving). */
    metadata?: Record<string, unknown>;
}

interface SidebarProps {
    onSelectSection: (sectionIndex: number) => void;
    sections: SidebarSection[];
    selectedSectionIndex: number;
    title: string;
}

/**
 * Sidebar component for navigating between sections in the questionnaire.
 * Displays Overview, control groups, and Submit sections.
 */
const Sidebar = (props: SidebarProps): JSX.Element => {
    // Whenever a new section is selected, scroll to the top of the page rather than maintaining page scroll position across sections.
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [props.selectedSectionIndex]);

    return (
        <div className={styles.container}>
            <div className={styles.sidebar}>
                <div className={styles.sidebarHeader}>
                    <Text variant="Header3" noStyles>
                        <span className={styles.textActive}>{props.title}</span>
                    </Text>
                </div>
                <div className={styles.groupList}>
                    {props.sections.map((section, index) => {
                        const isSelected = index === props.selectedSectionIndex;

                        return (
                            <button key={index} className={`${styles.groupItem} ${isSelected ? styles.selected : ''}`} onClick={() => props.onSelectSection(index)} type="button">
                                <div className={styles.groupItemContent}>
                                    <div className={styles.groupNameRow}>
                                        <Text variant="Text2" noStyles>
                                            <span className={isSelected ? styles.textActive : styles.textInactive}>{section.title}</span>
                                        </Text>
                                    </div>
                                    {section.additionalContent ? section.additionalContent : <></>}
                                </div>
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};
